import { SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../../components/ui/common/button";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/common/popover";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/common/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import { productOrderByOptions } from "../../../../config/constants";
import { CalculationFilterStatus } from "../../../../config/enums/ProductType.enum";
import { useProductFilterState } from "../hooks/useProductFilterState";

const SORT_OPTIONS = [
  { value: CalculationFilterStatus.CALCULATED, label: "With calculation" },
  { value: CalculationFilterStatus.NOT_CALCULATED, label: "With out calculation" },
  { value: CalculationFilterStatus.NONE, label: "Not relevant" }
] as const;

interface ProductFilterPanelProps {
  refetchWithFilters: (calculateStatus: CalculationFilterStatus, orderBy: string) => void;
}

/**
 * Tailwind replacement for the legacy Semantic UI `ProductFilter` popup (see
 * src/screens/product/productList/ProductFilter.tsx) — same calculation
 * status radio group + order-by dropdown + Reset/Apply, rebuilt on the
 * common `Popover`/`RadioGroup`/`Select` primitives instead of
 * `semantic-ui-react`. Styled after `IngredientFilterPanel`
 * (src/screens/lcaIngredients/ingredientList/components/IngredientFilterPanel.tsx).
 */
const ProductFilterPanel = ({ refetchWithFilters }: ProductFilterPanelProps) => {
  const [open, setOpen] = useState(false);
  const filterState = useProductFilterState(refetchWithFilters);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="tw-shrink-0 tw-gap-2">
          <SlidersHorizontal size={16} />
          Filters
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="tw-w-72">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-text-sm tw-font-medium tw-text-foreground">Sort Result</p>
            <RadioGroup
              value={filterState.calculateStatus}
              onValueChange={value => filterState.setCalculateStatus(value as CalculationFilterStatus)}
              className="tw-flex tw-flex-col tw-gap-2"
            >
              {SORT_OPTIONS.map(option => (
                <label
                  key={option.value}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-foreground"
                >
                  <RadioGroupItem value={option.value} />
                  {option.label}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-text-sm tw-font-medium tw-text-foreground">Order By</p>
            <Select value={filterState.orderBy} onValueChange={filterState.setOrderBy}>
              <SelectTrigger className="!tw-h-9 tw-w-full tw-text-sm">
                <SelectValue placeholder="Select Order" />
              </SelectTrigger>
              <SelectContent>
                {productOrderByOptions.map((option: any) => (
                  <SelectItem key={option.key} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-border tw-pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                filterState.reset();
                setOpen(false);
              }}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={() => {
                filterState.apply();
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ProductFilterPanel;
